import React from 'react'
import { Button, Select } from '@asad/ui'
import { useMap } from '@vis.gl/react-google-maps'
import { ArrowBigLeftDashIcon, ArrowBigRightDashIcon, PowerOffIcon } from 'lucide-react'
import { clsx } from 'clsx'
import { t } from 'i18next'

import { SelectItemType } from '@/features/separateMap/components/SeparateMap'
import { Device } from '@/api/profile.api'

import { useMapPosition } from '../../hooks'
import { DeviceStatusType } from '../Dashboard'
import { Map, MapMarkers } from '.'

type MapContainerProps = {
  devicesStatuses: DeviceStatusType[]
  visibleDevices: Device[]
  clickedChip: undefined | string
  handleChipClick: (deviceId: string) => void
  onPinClick?: (deviceId: string) => void
  isSeparateMap?: boolean
  isMapDetached?: boolean
  setIsMapDetached?: React.Dispatch<React.SetStateAction<boolean>>
  selectItems?: SelectItemType[]
  selectedValue?: SelectItemType
  handleSelectChange?: (value: SelectItemType) => void
}

const MapContainer = ({
  devicesStatuses,
  visibleDevices,
  clickedChip,
  handleChipClick,
  onPinClick,
  isSeparateMap,
  isMapDetached,
  setIsMapDetached,
  selectItems,
  handleSelectChange,
  selectedValue,
}: MapContainerProps) => {
  const map = useMap()
  const { cameraRef, handleCameraChanged } = useMapPosition()

  const memoizedMarkers = React.useMemo(() => {
    return (
      <MapMarkers
        visibleDevices={visibleDevices}
        devicesStatuses={devicesStatuses}
        clickedChip={clickedChip}
        onPinClick={onPinClick}
      />
    )
  }, [visibleDevices, devicesStatuses, clickedChip])

  const handleDetachMap = () => {
    const screenWidth = window.screen.availWidth
    const screenHeight = window.screen.availHeight
    const width = Math.floor(screenWidth / 2)
    const height = screenHeight
    const left = screenWidth - width

    window.open('/separated-map', 'DetachedMapWindow', `width=${width},height=${height},left=${left},top=0`)

    localStorage.setItem('isMapDetached', 'true')

    setIsMapDetached?.(true)
  }

  const handleRestoreMap = () => {
    const bc = new BroadcastChannel('map-channel')
    bc.postMessage({ type: 'FORCE_CLOSE_DETACHED_MAP' })
    bc.close()

    localStorage.setItem('isMapDetached', 'false')
    setIsMapDetached?.(false)
  }

  return (
    <div
      className={clsx('w-full px-4 2xl:px-8 py-8 flex flex-col gap-4 shadow-deep-light dark:shadow-deep-dark', {
        'h-screen bg-background-general-light dark:bg-background-general-dark': isSeparateMap,
        'rounded-lg bg-background-wrapper-light dark:bg-background-wrapper-dark': !isSeparateMap,
      })}
    >
      <div className='flex justify-end w-full'>
        {!isSeparateMap ? (
          <Button variant='primary' onClick={handleDetachMap} icon={<ArrowBigRightDashIcon />} size='xl'>
            {t('main.openInNewTab')}
          </Button>
        ) : (
          <div className='w-full flex flex-wrap-reverse justify-between items-center gap-4'>
            <Select
              items={selectItems ?? []}
              selected={selectedValue ?? { name: '', id: '' }}
              //@ts-ignore
              setSelected={handleSelectChange ?? (() => {})}
              className='!max-w-80 !w-full'
            />
            <Button variant='primary' onClick={handleRestoreMap} icon={<ArrowBigLeftDashIcon />} size='xl'>
              {t('main.restoreToDashboard')}
            </Button>
          </div>
        )}
      </div>
      {!isMapDetached && (
        <>
          <div
            className={clsx('relative rounded-lg overflow-hidden', {
              'h-full': isSeparateMap,
            })}
          >
            <Map cameraRef={cameraRef} handleCameraChanged={handleCameraChanged} isSeparateMap={isSeparateMap}>
              {memoizedMarkers}
            </Map>
          </div>

          <div className='flex flex-wrap gap-3'>
            {visibleDevices?.map((device) => {
              const { alertStatus, isWorking } = devicesStatuses?.find(({ id }) => id === device.id) || {}

              let variant: 'success' | 'warning' | 'destructive'

              if (alertStatus === 'normal') {
                variant = 'success'
              } else if (alertStatus === 'alarm') {
                variant = 'destructive'
              } else variant = 'warning'

              return (
                <div key={device?.id} className='relative'>
                  <Button
                    variant={variant}
                    icon={isWorking ? null : <PowerOffIcon size={20} />}
                    size='lg'
                    onClick={() => {
                      const center = {
                        lat: device?.latitude || 52.22977,
                        lng: device?.longitude || 21.01178,
                      }
                      handleChipClick(device?.id)
                      map?.panTo(center)
                      cameraRef.current.center = center
                    }}
                  >
                    {device?.name}
                    {device?.id === clickedChip && (
                      <>
                        <span className='absolute inset-customBorder border-2 rounded-md border-info-border animate-clippath z-0 pointer-events-none'></span>
                        <span className='absolute inset-customBorder border-2 rounded-md border-info-border animate-clippathReverse z-0 pointer-events-none'></span>
                      </>
                    )}
                  </Button>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

export default MapContainer
